import { Box, CircularProgress } from "@mui/material";
import {
  ChevronRight as PrevIcon,
  ChevronLeft as NextIcon,
  NoMealsOutlined as EmptyIcon,
  LockOutlined as ReadOnlyIcon,
} from "@mui/icons-material";
import { useCallback, useMemo, useState } from "react";
import moment from "moment";
import { v2, nums } from "../ui/v2Tokens";
import { Card, EmptyState, Pill, TextButton } from "../ui/primitives";
import { bandTone, bandWord } from "../ui/bandTone";
import { dayBand, dayDeviationPct } from "../builder/targetsMath";
import { useDayLogV2 } from "./useDayLogV2";
import { DayPlanView } from "./DayPlanView";
import { useMonthLogV2 } from "./useMonthLogV2";
import MonthGrid from "./MonthGrid";
import MonthLegend from "./MonthLegend";
import MonthSummaryLine from "./MonthSummaryLine";
import { LOG_METRICS, metricLabel, metricValue, monthSummary, type LogMetric } from "./monthMetrics";
import { useNutritionDisplayMode } from "../../../../contexts/NutritionDisplayContext";
import { KEY, addMonths, dayHeading, monthLabel, monthStartOf } from "./hebrewDates";

type Props = { traineeId: string };

/**
 * The trainee's eating log, a month at a glance with one day open. READ-ONLY on purpose — this is
 * what the trainee reported, and nothing here is the trainer's to change.
 *
 * ── why a month, and what that costs ──
 *
 * It used to page a week at a time, which answers "what did they eat on Tuesday" and cannot answer
 * the question a trainer actually opens this screen for: are they logging at all, and where are they
 * drifting? Seven circles show no pattern. So the screen runs TWO fetches — a month of per-day
 * summaries for the grid (`useMonthLogV2`) and the open day in full (`useDayLogV2`). Their loading
 * and error states are kept apart deliberately: a month that fails must not blank the day being
 * read, and a failed day must not blank the calendar.
 *
 * ── what the open day shows ──
 *
 * `DayPlanView`: the menu the trainee was assigned AND what they did to it. This file used to list
 * the foods they ticked, and carried a note explaining that swaps, additions, the alternative meal
 * chosen, water and supplement ticks "have no source at all" — true of V1, where the plan and the
 * actual were the same rows. They have a source now. See `dayLogV2Model`.
 *
 * ── the trap ──
 *
 * A day the trainee did NOT report still carries a plan, and its totals can look exactly like
 * intake. Every surface here that could imply adherence — the grid cell, the month average, the
 * coverage bar, the composition card's caption — keys off `reported` rather than off the numbers.
 */
const FoodLogV2 = ({ traineeId }: Props) => {
  const today = useMemo(() => moment().format(KEY), []);
  const [selected, setSelected] = useState(today);
  const [viewMonth, setViewMonth] = useState(() => monthStartOf(today));
  const [metric, setMetric] = useState<LogMetric>("calories");
  const mode = useNutritionDisplayMode();

  const { days: monthDays, loading: monthLoading, error: monthError } = useMonthLogV2(
    traineeId,
    viewMonth
  );
  const { day: dayLog, loading, error } = useDayLogV2(traineeId, selected);

  const daysInMonth = useMemo(() => moment(viewMonth, KEY).daysInMonth(), [viewMonth]);
  const summary = useMemo(
    () => monthSummary(monthDays, metric, viewMonth, today, daysInMonth),
    [monthDays, metric, viewMonth, today, daysInMonth]
  );

  /*
   * The month browses independently of the open day, so a trainer can look back through a quarter
   * without losing the day they were reading. When `selected` is outside `viewMonth` no cell is
   * ringed — which is honest, and the heading below always states the full date, so there is never
   * a question about which day the log belongs to.
   */
  const shiftMonth = useCallback(
    (direction: -1 | 1) => {
      const next = addMonths(viewMonth, direction);
      if (next > monthStartOf(today)) return; // never page into a month that hasn't happened
      setViewMonth(next);
    },
    [viewMonth, today]
  );

  const isThisMonth = viewMonth === monthStartOf(today);

  // The open day's own verdict, from the same rule that tints its cell above. Stated in words so the
  // tiles beneath read as detail under a verdict rather than as a rival one — they answer a
  // different question (how far past this macro) with a tighter rule.
  const selectedDay = monthDays.find((day) => day.date === selected);
  const selectedBand =
    selectedDay?.reported
      ? dayBand(metricValue(selectedDay.totals, metric), metricValue(selectedDay.targets, metric))
      : null;
  const selectedDeviation =
    selectedDay?.reported
      ? dayDeviationPct(metricValue(selectedDay.totals, metric), metricValue(selectedDay.targets, metric))
      : null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "14px" }}>
      {/* ── the month ─────────────────────────────────────────────────────────────────────── */}
      <Card>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            flexWrap: "wrap",
            padding: "10px 14px",
            borderBottom: `1px solid ${v2.lineSoft}`,
          }}
        >
          <TextButton onClick={() => shiftMonth(-1)} sx={{ padding: "5px 10px" }}>
            <PrevIcon sx={{ fontSize: 17 }} />
            חודש קודם
          </TextButton>
          <TextButton
            onClick={() => shiftMonth(1)}
            disabled={isThisMonth}
            sx={{ padding: "5px 10px" }}
          >
            חודש הבא
            <NextIcon sx={{ fontSize: 17 }} />
          </TextButton>
          {/* `unicodeBidi: isolate` on the label: a Latin-digit year after a Hebrew word inside an
              RTL paragraph can be reordered by the bidi algorithm and land on the wrong side. */}
          <Box
            sx={{
              fontSize: "13px",
              color: v2.ink,
              fontWeight: 700,
              unicodeBidi: "isolate",
              ...nums,
            }}
          >
            {monthLabel(viewMonth)}
          </Box>

          <Box sx={{ flex: 1 }} />

          <Box role="group" aria-label="מדד להצגה בלוח" sx={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
            {LOG_METRICS.map((m) => (
              <Pill
                key={m.key}
                active={m.key === metric}
                ariaPressed={m.key === metric}
                onClick={() => setMetric(m.key)}
              >
                {metricLabel(m.key, mode)}
              </Pill>
            ))}
          </Box>

          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "5px",
              fontSize: "11px",
              color: v2.ink3,
              fontWeight: 600,
            }}
          >
            <ReadOnlyIcon sx={{ fontSize: 13 }} />
            לקריאה בלבד — דיווח המתאמן
          </Box>
        </Box>

        {monthLoading ? (
          <Box
            data-testid="month-log-loading"
            sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1.5, padding: "56px" }}
          >
            <CircularProgress size={22} sx={{ color: v2.brand }} />
            <Box sx={{ fontSize: 13, color: v2.ink3 }}>טוען את החודש…</Box>
          </Box>
        ) : monthError ? (
          <Box
            data-testid="month-log-error"
            sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1, padding: "48px" }}
          >
            <Box sx={{ fontSize: 14, fontWeight: 500, color: v2.over }}>לא הצלחנו לטעון את החודש</Box>
            <Box sx={{ fontSize: 12.5, color: v2.ink3 }}>בדוק את החיבור ונסה שוב</Box>
          </Box>
        ) : (
          <Box sx={{ padding: "12px 14px 14px" }}>
            <MonthGrid
              monthStart={viewMonth}
              days={monthDays}
              metric={metric}
              selected={selected}
              today={today}
              onSelect={setSelected}
            />
            <MonthLegend />
          </Box>
        )}
      </Card>

      {!monthLoading && !monthError && (
        <Card>
          <MonthSummaryLine summary={summary} metric={metric} />
        </Card>
      )}

      {/* ── the open day ──────────────────────────────────────────────────────────────────── */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap", padding: "2px 2px 0" }}>
        <Box sx={{ fontSize: "15px", fontWeight: 800, color: v2.ink }}>{dayHeading(selected)}</Box>
        {selected === today && (
          <Box
            sx={{
              fontSize: "11.5px",
              fontWeight: 700,
              padding: "2px 9px",
              borderRadius: "7px",
              background: v2.brandWash,
              color: v2.brandText,
            }}
          >
            היום
          </Box>
        )}
        {selectedBand && selectedBand !== "none" && (
          <Box
            data-testid="day-band-tag"
            sx={{
              fontSize: "11.5px",
              fontWeight: 700,
              padding: "2px 9px",
              borderRadius: "7px",
              background: bandTone[selectedBand].fill,
              color: bandTone[selectedBand].text,
              ...nums,
            }}
          >
            {bandWord[selectedBand]}
            {selectedDeviation !== null && ` (${selectedDeviation > 0 ? "+" : ""}${selectedDeviation}%)`}
          </Box>
        )}
        {selected !== today && (
          <TextButton
            tone="green"
            onClick={() => {
              setSelected(today);
              setViewMonth(monthStartOf(today));
            }}
            sx={{ padding: "5px 12px" }}
          >
            חזור להיום
          </TextButton>
        )}
      </Box>

      {/* Three states. "Still loading" and "reported nothing" are very different claims about a
          trainee, and a failed request must not be shown as the second one. */}
      {loading ? (
        <Card>
          <Box
            data-testid="food-log-loading"
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.5,
              padding: "44px",
            }}
          >
            <CircularProgress size={26} sx={{ color: v2.brand }} />
            <Box sx={{ fontSize: 13, color: v2.ink3 }}>טוען יומן אכילה…</Box>
          </Box>
        </Card>
      ) : error ? (
        <Card>
          <Box
            data-testid="food-log-error"
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              padding: "44px",
            }}
          >
            <Box sx={{ fontSize: 15, fontWeight: 500, color: v2.over }}>לא הצלחנו לטעון את היומן</Box>
            <Box sx={{ fontSize: 13, color: v2.ink3 }}>בדוק את החיבור ונסה שוב</Box>
          </Box>
        </Card>
      ) : (
        <>
          {dayLog ? (
            <DayPlanView day={dayLog} isFuture={selected > today} />
          ) : (
            <Card>
              <EmptyState icon={EmptyIcon} text="אין נתונים ליום הנבחר." />
            </Card>
          )}
        </>
      )}
    </Box>
  );
};

export default FoodLogV2;
